import React, { useState, useEffect, useRef } from "react";
import styles from "./Deals.module.css";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/PageHeader";
import Loader from "../components/common/Loader";
import DealList from "../components/deals/DealList";
import DealNoteList from "../components/deals/DealNoteList";
import NoteList from "../components/notes/NoteList";
import NoteDetails from "../components/notes/NoteDetails";
import HistoryList from "../components/notes/HistoryList";
import ButtonGroup from "../components/common/ButtonGroup";
import { DeleteIcon } from "../assets/icons/DeleteIcon";
import SearchInput from "../components/common/SearchInput";
import { TableSkeleton } from "../components/common/Skeleton";
import { confirmDialog } from "../components/common/ConfirmDialog";
import EmptyState from "../components/common/EmptyState";

const PORT = process.env.REACT_APP_PORT;

// Extract date from filename (e.g. "2023-02-23 16-45-32 BANK 2023-BNK45")
const extractDateFromFilename = (filename) => {
  if (!filename) return null;
  const match = filename.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute, second] = match;
  const dateStr = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : dateStr;
};

const Deals = () => {
  const { role, userInfo } = useAuth();
  const [dealData, setDealData] = useState([]);
  const [dealNotesData, setDealNotesData] = useState([]);
  const [noteData, setNoteData] = useState([]);
  const [showDealTable, setShowDealTable] = useState(true);
  const [showLoanTable, setShowLoanTable] = useState(true); // In Deals context, this is DealNoteList
  const [showNoteTable, setShowNoteTable] = useState(true);
  const [showHistoryTable, setShowHistoryTable] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isLoadingDealNotes, setIsLoadingDealNotes] = useState(false);
  const [isLoadingDeals, setIsLoadingDeals] = useState(true);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [showEditDealModal, setShowEditDealModal] = useState(false);
  const [isSavingDeal, setIsSavingDeal] = useState(false);
  const [editDealFormData, setEditDealFormData] = useState({
    dealId: "",
    deal: "",
    dealCIK: "",
    depositorName: "",
    depositorCIK: "",
    accessionNumber: "",
    primaryDocument: "",
    documentDate: "",
    closingDate: "",
    filingDate: "",
    createdAt: "",
  });
  const [uploadedNotesForDeal, setUploadedNotesForDeal] = useState([]);
  const dealNotesFileInputRef = useRef(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [uploadAcceptanceDate, setUploadAcceptanceDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const matchesSearch = (row) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return JSON.stringify(row || "").toLowerCase().includes(q);
  };
  const filteredDeals = Array.isArray(dealData) ? dealData.filter(matchesSearch) : dealData;
  const filteredDealNotes = Array.isArray(dealNotesData) ? dealNotesData.filter(matchesSearch) : dealNotesData;
  const filteredNotes = Array.isArray(noteData) ? noteData.filter(matchesSearch) : noteData;

  useEffect(() => {
    if (role === "admin") {
      fetchDeals(searchTerm);
    } else if (userInfo?.email) {
      fetchMyDeals(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, userInfo, searchTerm]);

  const fetchDeals = async (q = "") => {
    setIsLoadingDeals(true);
    try {
      const response = await axios.get(`${PORT}getAllDeals`, { params: q ? { q } : {} });
      setDealData(response.data.filter((deal) => !deal.isEdited));
    } catch (error) {
      console.error("Error fetching deals:", error);
      toast.error("Failed to fetch deals");
    } finally {
      setIsLoadingDeals(false);
    }
  };

  const fetchMyDeals = async (q = "") => {
    setIsLoadingDeals(true);
    try {
      const response = await axios.post(`${PORT}getMyDeals`, q ? { q } : {});
      setDealData(response.data.filter((deal) => !deal.isEdited));
    } catch (error) {
      console.error("Error fetching my deals:", error);
      toast.error("Failed to fetch your deals");
    } finally {
      setIsLoadingDeals(false);
    }
  };

  const fetchDealNotes = async (dealId) => {
    try {
      setIsLoadingDealNotes(true);
      const response = await axios.post(`${PORT}getDealNotes`, { dealId });
      setDealNotesData(response.data);
    } catch (error) {
      console.error("Error fetching deal notes:", error);
      toast.error("Failed to fetch deal notes");
    } finally {
      setIsLoadingDealNotes(false);
    }
  };

  const fetchNotes = async (loanId) => {
    try {
      setIsLoadingNotes(true);
      const response = await axios.post(`${PORT}getAllNotes`, { loanId });
      setNoteData(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Failed to fetch notes");
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().split("T")[0];
  };

  /** Value for `input type="datetime-local"` (local calendar date + time, no timezone suffix). */
  const formatDateTimeLocalForInput = (dateValue) => {
    if (!dateValue) return "";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return "";
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    const h = String(parsed.getHours()).padStart(2, "0");
    const min = String(parsed.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${d}T${h}:${min}`;
  };

  const openEditDealModal = (deal) => {
    setEditDealFormData({
      dealId: deal.id,
      deal: deal.deal || "",
      dealCIK: deal.dealCIK || "",
      depositorName: deal.depositorName || "",
      depositorCIK: deal.depositorCIK || "",
      accessionNumber: deal.accessionNumber || "",
      primaryDocument: deal.primaryDocument || "",
      documentDate: formatDateForInput(deal.documentDate),
      closingDate: formatDateForInput(deal.closingDate),
      filingDate: formatDateForInput(deal.filingDate),
      createdAt: formatDateTimeLocalForInput(deal.createdAt),
    });
    setUploadedNotesForDeal([]);
    setUploadAcceptanceDate(null);
    setShowEditDealModal(true);
  };

  const handleEditDealChange = (e) => {
    const { name, value } = e.target;
    setEditDealFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditDealSubmit = async (e) => {
    e.preventDefault();
    setIsSavingDeal(true);
    try {
      const payload = {
        dealId: editDealFormData.dealId,
        deal: editDealFormData.deal,
        dealCIK: editDealFormData.dealCIK,
        depositorName: editDealFormData.depositorName,
        depositorCIK: editDealFormData.depositorCIK,
        accessionNumber: editDealFormData.accessionNumber,
        primaryDocument: editDealFormData.primaryDocument,
        documentDate: editDealFormData.documentDate,
        closingDate: editDealFormData.closingDate,
        filingDate: editDealFormData.filingDate,
        ...(editDealFormData.createdAt
          ? {
              createdAt: new Date(editDealFormData.createdAt).toISOString(),
            }
          : {}),
      };
      if (uploadedNotesForDeal.length > 0) {
        payload.notes = uploadedNotesForDeal.map((row) => ({
          loan: row.loan,
          loanParent: row.loanParent || undefined,
          note: row.note,
          control: row.control,
          balance: row.balance,
          balanceDescription: row.balanceDescription || "",
          holder: row.holder,
          holderParent: row.holderParent || undefined,
          holderCIK: row.holderCIK || undefined,
          accessionNumber: row.accessionNumber || undefined,
          primaryDocument: row.primaryDocument || undefined,
          filingDate: row.filingDate || undefined,
          documentDate: row.documentDate || undefined,
          closingDate: row.closingDate || undefined,
        }));
      }
      const response = await axios.post(`${PORT}editDeal`, payload);

      if (response.data?.success) {
        const notesCount = response.data.notesCreated ?? 0;
        toast.success(
          notesCount > 0
            ? `Deal updated and ${notesCount} note(s) added.`
            : "Deal updated successfully"
        );
        setShowEditDealModal(false);
        setEditDealFormData({
          dealId: "",
          deal: "",
          dealCIK: "",
          depositorName: "",
          depositorCIK: "",
          accessionNumber: "",
          primaryDocument: "",
          documentDate: "",
          closingDate: "",
          filingDate: "",
          createdAt: "",
        });
        setUploadedNotesForDeal([]);
        setUploadAcceptanceDate(null);
        if (role === "admin") {
          fetchDeals();
        } else {
          fetchMyDeals();
        }
        if (selectedDealId === Number(editDealFormData.dealId) && response.data.newDealId) {
          setSelectedDealId(response.data.newDealId);
          fetchDealNotes(response.data.newDealId);
        }
      } else {
        toast.error(response.data?.message || "Failed to update deal");
      }
    } catch (error) {
      console.error("Error updating deal:", error);
      toast.error("Failed to update deal");
    } finally {
      setIsSavingDeal(false);
    }
  };

  const handleDealSelection = (dealId) => {
    setSelectedDealId(dealId);
    fetchDealNotes(dealId);
    setShowDealTable(false);
    setShowLoanTable(true);
    setShowNoteTable(true);
  };

  const handleLoanSelection = (loanId) => {
    // Selecting a loan shows notes for that loan
    fetchNotes(loanId);
    setShowLoanTable(false);
    setShowNoteTable(true);
  };

  const handleNoteSelection = (note) => {
    setSelectedNote(note);
    setShowNoteTable(false); // Show Details
  };

  const removeDealSelection = () => {
    setSelectedDealId(null);
    setShowDealTable(true);
    setShowLoanTable(true);
    setShowNoteTable(true); // Reset
    setNoteData([]);
  };

  const removeLoanSelection = () => {
    // Back to DealNoteList
    setShowLoanTable(true);
    setShowNoteTable(true); // Reset
    setNoteData([]);
  };

  const removeNoteSelection = () => {
    // Back to NoteList
    setSelectedNote(null);
    setShowNoteTable(true);
  };

  const showHistory = async (selectedNote) => {
    try {
      const response = await axios.post(`${PORT}getNoteHistory`, {
        selectedNote,
      });
      setShowHistoryTable(true);
      setHistoryData(response.data);
    } catch (error) {
      console.error("Error fetching note history:", error);
      toast.error("Failed to fetch note history");
    }
  };

  const removeHistorySelection = () => {
    setShowHistoryTable(false);
    setHistoryData([]);
  };

  const handleDealNotesFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { cellDates: true });

        const parseExcelDate = (dateVal) => {
          if (!dateVal) return "";
          if (dateVal instanceof Date) return dateVal;
          if (typeof dateVal === "number") {
            return new Date((dateVal - 25569) * 86400 * 1000);
          }
          return new Date(dateVal);
        };

        const transformed = jsonData.map((row) => ({
          loan: row.loan ?? row.Loan ?? "",
          loanParent: row.loanParent ?? row["Loan Parent"] ?? "",
          note: row.note ?? row.Note ?? "",
          control: row.control ?? row.Control ?? "Non-Control",
          balance: row.balance ?? row.Balance ?? "",
          balanceDescription: row.balanceDescription ?? row["Balance Description"] ?? "",
          holder: row.holder ?? row.Holder ?? "",
          holderParent: row.holderParent ?? row["Holder Parent"] ?? "",
          holderCIK: row.holderCIK ?? row["Holder CIK"] ?? "",
          accessionNumber: row.accessionNumber ?? row["Accession Number"] ?? "",
          primaryDocument: row.primaryDocument ?? row["Primary Document"] ?? "",
          filingDate: parseExcelDate(row.filingDate),
          documentDate: parseExcelDate(row.documentDate),
          closingDate: parseExcelDate(row.closingDate),
        }));

        const withRequired = transformed.filter(
          (r) => (r.loan && r.note && r.holder)
        );
        if (withRequired.length === 0) {
          toast.error("No valid rows found. Each row must have Loan, Note, and Holder.");
          return;
        }
        setUploadAcceptanceDate(extractDateFromFilename(file.name));
        setUploadedNotesForDeal(withRequired);
      } catch (err) {
        console.error("Error parsing Excel:", err);
        toast.error("Error parsing file. Check the format.");
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  const handleDeleteDeal = async (deal) => {
    const ok = await confirmDialog({
      title: "Delete deal?",
      message: `This will remove "${deal.deal}" along with all its reports and notes. This cannot be undone.`,
      confirmLabel: "Delete deal",
      danger: true,
    });
    if (!ok) return;
    try {
      const response = await axios.post(`${PORT}deleteDeal`, { dealId: deal.id });
      if (response.data?.success) {
        toast.success("Deal deleted successfully");
        if (selectedDealId === deal.id) removeDealSelection();
        if (role === "admin") fetchDeals();
        else fetchMyDeals();
      } else {
        toast.error(response.data?.message || "Failed to delete deal");
      }
    } catch (error) {
      console.error("Error deleting deal:", error);
      toast.error(error.response?.data?.message || "Failed to delete deal");
    }
  };

  const selectedDeal = selectedDealId != null ? dealData.find((d) => d.id === selectedDealId) : null;
  const acceptanceDate = selectedDeal?.acceptanceDate || null;

  return (
    <div>
      <PageHeader pageName="Deals" />
      {isLoadingDealNotes ? (
        <TableSkeleton rows={6} cols={6} />
      ) : (
        <div className="main-page-content fade-in">
          <div className="page-toolbar">
            <h2 className="page-toolbar-title">
              {showDealTable ? "Deals" : showLoanTable ? "Deal notes" : showNoteTable ? "Notes" : "Note details"}
            </h2>
            <div className="page-toolbar-actions">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search deals, depositors, CIK..."
              />
            </div>
          </div>
          {showDealTable ? (
            isLoadingDeals ? (
              <TableSkeleton rows={8} cols={11} />
            ) : filteredDeals.length === 0 ? (
              <EmptyState
                icon="bi-briefcase"
                title={searchTerm ? "No deals match your search" : "No deals yet"}
                subtitle={searchTerm
                  ? "Try a different deal name, CIK, or depositor."
                  : "Deals will appear once they are added or synced from EDGAR."}
              />
            ) : (
              <DealList
                dealData={filteredDeals}
                handleDealSelection={handleDealSelection}
                onEditDeal={openEditDealModal}
                onDeleteDeal={handleDeleteDeal}
              />
            )
          ) : showLoanTable ? (
            <>
              <DealNoteList
                dealNotesData={filteredDealNotes}
                handleLoanSelection={handleLoanSelection}
                acceptanceDate={acceptanceDate}
              />
              <div className="button-group ">
                <button className="btn btn-ghost" onClick={removeDealSelection}>
                  <i className="bi bi-arrow-left" /> Back to deal list
                </button>
              </div>
            </>
          ) : isLoadingNotes ? (
            <TableSkeleton rows={5} cols={6} />
          ) : !showNoteTable ? (
            showHistoryTable ? (
              <HistoryList historyData={historyData} />
            ) : (
              <NoteDetails selectedNote={selectedNote} />
            )
          ) : (
            <NoteList
              noteData={filteredNotes}
              handleNoteSelection={handleNoteSelection}
              acceptanceDate={acceptanceDate}
            />
          )}

          {!showDealTable && !showLoanTable && (
            <ButtonGroup
              showNoteTable={showNoteTable}
              showHistoryTable={showHistoryTable}
              removeLoanSelection={removeLoanSelection}
              removeNoteSelection={removeNoteSelection}
              removeHistorySelection={removeHistorySelection}
              showHistory={showHistory}
              showLoanTable={false} // In Deals context, this controls "Back to Loan List" button which actually goes to DealNoteList
              selectedNote={selectedNote}
            />
          )}
        </div>
      )}

      {showEditDealModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "800px", backgroundColor: "white" }}>
            <button
              className="close-button"
              onClick={() => {
                setShowEditDealModal(false);
                setUploadedNotesForDeal([]);
                setUploadAcceptanceDate(null);
              }}
              disabled={isSavingDeal}
            >
              ×
            </button>
            <h3>Edit Deal</h3>
            {isSavingDeal && (
              <div style={{ marginTop: "10px", color: "#152c70", fontWeight: "600" }}>
                Please wait, saving deal
                {uploadedNotesForDeal.length > 0 ? " and adding notes" : ""}…
              </div>
            )}
            <form onSubmit={handleEditDealSubmit}>
              <div
                className="note-form"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                  marginTop: "20px",
                }}
              >
                <div className="form-group">
                  <label>Deal Name</label>
                  <input
                    type="text"
                    name="deal"
                    value={editDealFormData.deal}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Deal CIK</label><br/>
                  <input
                    type="text"
                    name="dealCIK"
                    value={editDealFormData.dealCIK}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Depositor Name</label>
                  <input
                    type="text"
                    name="depositorName"
                    value={editDealFormData.depositorName}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Depositor CIK</label>
                  <input
                    type="text"
                    name="depositorCIK"
                    value={editDealFormData.depositorCIK}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Accession Number</label>
                  <input
                    type="text"
                    name="accessionNumber"
                    value={editDealFormData.accessionNumber}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Primary Document</label>
                  <input
                    type="text"
                    name="primaryDocument"
                    value={editDealFormData.primaryDocument}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Document Date</label>
                  <input
                    type="date"
                    name="documentDate"
                    value={editDealFormData.documentDate}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Closing Date</label>
                  <input
                    type="date"
                    name="closingDate"
                    value={editDealFormData.closingDate}
                    onChange={handleEditDealChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Created date &amp; time</label>
                  <input
                    type="datetime-local"
                    name="createdAt"
                    value={editDealFormData.createdAt}
                    onChange={handleEditDealChange}
                    className="form-input"
                    step="1"
                  />
                </div>
              </div>
              {uploadedNotesForDeal.length > 0 && (
                <div style={{ marginTop: "20px", borderTop: "1px solid #eee", paddingTop: "16px" }}>
                  <h4 style={{ marginBottom: "8px" }}>Review notes to add to this deal</h4>
                  <p style={{ marginBottom: "12px", fontSize: "14px", color: "#555" }}>
                    These notes will be added when you click Save Changes below.
                  </p>
                  <div
                    style={{
                      maxHeight: "280px",
                      overflowY: "auto",
                      marginBottom: "12px",
                    }}
                  >
                    <table className="note-table">
                      <thead>
                        <tr>
                          <th>Loan</th>
                          <th>Holder</th>
                          <th>Note</th>
                          <th>Control</th>
                          <th>Balance</th>
                          <th>Balance Description</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadedNotesForDeal.map((row, index) => (
                          <tr key={index}>
                            <td>{row.loan}</td>
                            <td>{row.holder}</td>
                            <td>{row.note}</td>
                            <td>{row.control}</td>
                            <td>{row.balance}</td>
                            <td>{row.balanceDescription || "—"}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() =>
                                  setUploadedNotesForDeal((prev) =>
                                    prev.filter((_, i) => i !== index)
                                  )
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "4px",
                                }}
                                aria-label="Remove row"
                              >
                                <DeleteIcon />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div
                className="form-actions"
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "space-between",
                  marginTop: "20px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <input
                    ref={dealNotesFileInputRef}
                    type="file"
                    id="deal-notes-file-upload"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleDealNotesFileUpload}
                  />
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => dealNotesFileInputRef.current?.click()}
                    disabled={isSavingDeal}
                    style={{ backgroundColor: "#0d6efd", color: "white" }}
                  >
                    Upload Excel for notes
                  </button>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="action-button cancel-button"
                    onClick={() => {
                      setShowEditDealModal(false);
                      setUploadedNotesForDeal([]);
                      setUploadAcceptanceDate(null);
                    }}
                    disabled={isSavingDeal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="action-button add-button"
                    disabled={isSavingDeal}
                    style={{ backgroundColor: "#152c70", color: "white" }}
                  >
                    {isSavingDeal
                      ? "Saving..."
                      : uploadedNotesForDeal.length > 0
                        ? `Save deal and add ${uploadedNotesForDeal.length} note(s)`
                        : "Save Changes"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Deals;
